import { compact, isEqual } from 'lodash-es';
import { useWatch } from 'react-hook-form';

import useGetCompanyVerificationInfo from '../../../../common/hooks/useGetCompanyVerificationInfo';
import { VerificationMethod } from '../../../../common/interfaces/entities';
import {
  useCompanyVerificationFormContext,
  useCompanyVerificationFormState,
} from '../FormContext';

export default function useSubmitButtonEnabled() {
  const { isValid } = useCompanyVerificationFormState();
  const { control } = useCompanyVerificationFormContext();
  const { verificationInfo } = useGetCompanyVerificationInfo();
  const [verificationMethod, legalDocuments, verifyWithProofOfOwnership] =
    useWatch({
      control,
      name: [
        'verificationMethod',
        'verifyWithLegalDocuments',
        'verifyWithProofOfOwnership',
      ],
    });

  const lastVerificationRequest = verificationInfo?.lastVerificationRequest;

  if (!verificationInfo?.lastVerificationRequest && isValid) {
    return true;
  }

  function sortArray(array: string[] | null) {
    return compact(array || []).sort();
  }

  if (
    verificationMethod === VerificationMethod.LEGAL_DOCUMENTS &&
    lastVerificationRequest?.document.verificationMethod ===
      VerificationMethod.LEGAL_DOCUMENTS
  ) {
    const isDataChanged = !isEqual(
      {
        ...legalDocuments,
        additionalDocuments: sortArray(legalDocuments.additionalDocuments),
      },
      {
        companyNPWPDoc:
          lastVerificationRequest.document.companyNpwpDocument ?? null,
        companyNPWPNumber:
          lastVerificationRequest.document.companyNpwpNumber ?? null,
        companyNIBNumber:
          lastVerificationRequest.document.companyNibNumber || '',
        additionalDocuments: sortArray(
          lastVerificationRequest.document.additionalDocuments
        ),
        acraBizFile: lastVerificationRequest.document.acraBizFile ?? null,
        companyUniqueEntityNumber:
          lastVerificationRequest.document.companyUniqueEntityNumber ?? null,
        companyLegalDoc:
          lastVerificationRequest.document.companyLegalDoc ?? null,
        companyLegalNumber:
          lastVerificationRequest.document.companyLegalNumber ?? null,
      }
    );
    return isDataChanged && isValid;
  }

  if (
    verificationMethod === VerificationMethod.PROOF_OF_OWNERSHIP &&
    lastVerificationRequest?.document.verificationMethod ===
      VerificationMethod.PROOF_OF_OWNERSHIP
  ) {
    const isDataChanged = !isEqual(
      {
        personalNPWPDoc: verifyWithProofOfOwnership.personalNPWPDoc,
        personalNPWPNumber: verifyWithProofOfOwnership.personalNPWPNumber,
        personalNIBNumber: verifyWithProofOfOwnership.personalNIBNumber,
        businessActivityImages: sortArray(
          verifyWithProofOfOwnership.businessActivityImages
        ),
        ktpDoc: verifyWithProofOfOwnership.ktpDoc,
        ktpNumber: verifyWithProofOfOwnership.ktpNumber,
        businessLocationType: verifyWithProofOfOwnership.businessLocationType,
        additionalDocuments: sortArray(
          verifyWithProofOfOwnership.additionalDocuments
        ),
      },
      {
        personalNPWPDoc:
          lastVerificationRequest.document.personalNpwpDocument || '',
        personalNPWPNumber:
          lastVerificationRequest.document.personalNpwpNumber || '',
        personalNIBNumber:
          lastVerificationRequest.document.personalNibNumber || '',
        businessActivityImages: sortArray(
          lastVerificationRequest.document.businessImages
        ),
        ktpDoc: lastVerificationRequest.document.ktpDoc || '',
        ktpNumber: lastVerificationRequest.document.ktpNumber || '',
        businessLocationType:
          lastVerificationRequest.document.businessLocationType,
        additionalDocuments: sortArray(
          lastVerificationRequest.document.additionalDocuments
        ),
      }
    );

    return isDataChanged && isValid;
  }

  return isValid;
}
